import { h, Component } from 'preact';
import { Text } from 'preact-i18n';

import { reasonLabel, isRevenueReason } from '../../lib/ammoReasons';

function formatDate(value) {
    if (!value) {
        return '';
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
        return '';
    }
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function formatRegistrationTime(sale) {
    const registeredDate = formatDate(sale.createdDate);
    if (!registeredDate) {
        return '';
    }
    const date = new Date(sale.createdDate);
    const pad = n => String(n).padStart(2, '0');
    const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    return registeredDate === formatDate(sale.saleDate) ? time : `${registeredDate} ${time}`;
}

function formatAmount(value) {
    return new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 2 }).format(Number(value) || 0);
}

/**
 * Ammo lines of a sale. Counter registrations hold exactly one line; webshop /
 * convention orders may mix a signup fee with one or more ammo products, so only
 * the lines flagged isAmmo count. Falls back to the first line for old data.
 */
export function ammoLinesOf(sale) {
    const lines = sale?.products || [];
    const ammo = lines.filter(line => line && line.isAmmo);
    if (ammo.length > 0) {
        return ammo;
    }
    return lines[0] ? [lines[0]] : [];
}

/** Webshop orders paid through Vipps checkout store 'vippsIntegration'; show it like a counter Vipps sale. */
export function paymentLabel(paymentMethod) {
    return paymentMethod === 'vippsIntegration' ? 'vipps' : (paymentMethod || '');
}

/** Gross amount of the given lines (line price is net, vat in percent). */
export function linesAmount(lines) {
    return lines.reduce((sum, line) => {
        const net = (Number(line.price) || 0) * (Number(line.qty) || 0);
        return sum + net * (1 + (Number(line.vat) || 0) / 100);
    }, 0);
}

class AmmoSaleList extends Component {
    render() {
        const {
            sales = [], products = [], canEdit = false, onEdit = () => {},
        } = this.props;
        // Old sale lines may carry an empty caliber snapshot; prefer the current product's caliber.
        const productsById = {};
        products.forEach(p => {
            productsById[p.id] = p;
        });
        if (sales.length === 0) {
            return (
                <div class='text-body-secondary text-center py-4'>
                    <Text id='ammoSale.empty'>No ammo sales in this period.</Text>
                </div>
            );
        }
        return (
            <div class='table-responsive'>
                <table class='table table-sm table-hover align-middle mb-0'>
                    <thead>
                        <tr>
                            <th><Text id='ammoSale.date'>Date</Text></th>
                            <th><Text id='ammoSale.member'>Member</Text></th>
                            <th><Text id='ammoSale.caliber'>Caliber</Text></th>
                            <th class='text-end'><Text id='ammoSale.qty-short'>Qty</Text></th>
                            <th><Text id='ammoSale.reason'>Reason</Text></th>
                            <th><Text id='ammoSale.payment'>Payment</Text></th>
                            <th class='text-end'><Text id='ammoSale.amount'>Amount</Text></th>
                            <th><Text id='ammoSale.seller'>Seller</Text></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sales.map(sale => {
                            const lines = ammoLinesOf(sale);
                            const calibers = [...new Set(lines.map(line => (
                                productsById[line.id]?.caliber || line.caliber || line.name || ''
                            )).filter(Boolean))];
                            const qty = lines.reduce((sum, line) => sum + (Number(line.qty) || 0), 0);
                            const visitor = (sale.visitors || [])[0] || {};
                            const revenue = isRevenueReason(sale.ammoReason);
                            // Webshop orders are read-only here (see patch-ammosale) and their
                            // totalPrice is not maintained, so the amount is taken from the ammo lines.
                            const imported = !!(sale.isWeborder || sale.ammoImported);
                            const editable = canEdit && !imported;
                            const amount = imported ? linesAmount(lines) : sale.totalPrice;
                            const registrationTime = formatRegistrationTime(sale);
                            return (
                                <tr
                                    key={sale.id}
                                    class={editable ? 'cursor-pointer' : ''}
                                    onClick={() => (editable ? onEdit(sale) : null)}
                                >
                                    <td class='text-nowrap'>
                                        {formatDate(sale.saleDate)}
                                        {!!registrationTime && (
                                            <small class='d-block text-body-secondary'>
                                                <Text id='ammoSale.registered-at' fields={{ time: registrationTime }}>
                                                    Registered {registrationTime}
                                                </Text>
                                            </small>
                                        )}
                                    </td>
                                    <td>{`${visitor.firstname || ''} ${visitor.lastname || ''}`.trim()}</td>
                                    <td>{calibers.join(', ')}</td>
                                    <td class='text-end'>{qty}</td>
                                    <td>
                                        <span class={`badge ${revenue ? 'bg-success' : 'bg-secondary'}`}>{reasonLabel(sale.ammoReason)}</span>
                                    </td>
                                    <td>{paymentLabel(sale.paymentMethod)}</td>
                                    <td class='text-end'>{formatAmount(amount)}</td>
                                    <td class='text-body-secondary small'>
                                        {imported ? <Text id='ammoSale.webshop'>Webshop</Text> : (sale.sellerName || '')}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }
}

export default AmmoSaleList;
